/**
 * Right panel — tab switch (mockup `rp-tabbar` + `setRpTab`, fsl-mocks
 * commit 88097d2). The full panel height goes to whichever view is active.
 *
 * ENG-340: shown on the Drones and OIs contexts, with two tabs — DAA Alerts
 * and Airband Radio. Unread badges accumulate per tab while another tab is
 * being viewed and clear when you open it. The old Events / Alerts / AI
 * Intelligence tabs are kept behind `LEGACY_PANEL_TABS`.
 *
 * "Debug Logs" is an extra pill, offered only while debugMode is on — it
 * replaces the old nested SidebarTabs so the panel never stacks two tab bars.
 */
import { AirbandPane } from '@/features/alerts/AirbandPane';
import { AlertsPane } from '@/features/alerts/AlertsPane';
import { DaaAlertsPane } from '@/features/alerts/DaaAlertsPane';
import { EventsPane } from '@/features/alerts/EventsPane';
import { routeByContext } from '@/features/alerts/routing';
import { AppsIntelligence } from '@/features/apps/AppsIntelligence';
import { InventoryIntelligence } from '@/features/chat/InventoryIntelligence';
import { ServicesIntelligence } from '@/features/chat/ServicesIntelligence';
import { DebugPanel } from '@/features/debug/DebugPanel';
import { LEGACY_PANEL_TABS, rightPanelShownIn } from '@/features/shell/rightPanelFlag';
import { useAlertFeedStore } from '@/lib/store/alertFeed';
import { useAlertsStore } from '@/lib/store/alerts';
import { useDaaAlertsStore } from '@/lib/store/daaAlerts';
import { useDronesStore } from '@/lib/store/drones';
import {
  APP_START_MS,
  type Context,
  type RightPanelTab,
  type RpFeedTab,
  useUiStore,
} from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

export function RightPanel({ ctx, debugOn }: { ctx: Context; debugOn: boolean }) {
  const tab = useUiStore((s) => s.rightPanelTab);
  const setTab = useUiStore((s) => s.setRightPanelTab);
  const markSeen = useUiStore((s) => s.markRpSeen);
  const lastSeen = useUiStore((s) => s.rpLastSeen[ctx]);
  const selectedDroneId = useDronesStore((s) => s.selectedId);
  const events = useAlertsStore((s) => s.items);
  const alerts = useAlertFeedStore((s) => s.items);
  const daaLog = useDaaAlertsStore((s) => s.log);
  const sidebar = rightPanelShownIn(ctx);

  // Debug pill can vanish (debug mode off, unsupported context) while selected.
  const effectiveTab: RightPanelTab = LEGACY_PANEL_TABS
    ? tab === 'debug' && !debugOn
      ? 'ai'
      : tab
    : !sidebar
      ? 'debug'
      : tab === 'airband' || (tab === 'debug' && debugOn)
        ? tab
        : 'daa';

  // Opening a feed tab — or carrying it into another context — marks that
  // context/tab pair as read; leaving it does too, so what arrived while you
  // were looking at it doesn't come back as unread.
  useEffect(() => {
    if (
      effectiveTab === 'events' ||
      effectiveTab === 'alerts' ||
      effectiveTab === 'daa' ||
      effectiveTab === 'airband'
    ) {
      markSeen(ctx, effectiveTab);
      return () => markSeen(ctx, effectiveTab);
    }
  }, [ctx, effectiveTab, markSeen]);

  const unread = (which: RpFeedTab) => {
    if (effectiveTab === which) return 0;
    // Airband Radio has no source yet, so nothing can be unread.
    if (which === 'airband') return 0;
    const since = lastSeen?.[which] ?? APP_START_MS;
    if (which === 'daa') return daaLog.filter((e) => e.kind !== 'resolved' && e.at > since).length;
    const feed = which === 'events' ? events : alerts;
    return feed.filter(
      (i) => routeByContext(i, ctx, selectedDroneId) && new Date(i.timestamp).getTime() > since,
    ).length;
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-(--color-border) shrink-0 overflow-x-auto">
        {!LEGACY_PANEL_TABS && sidebar && (
          <>
            <TabPill
              label="DAA Alerts"
              on={effectiveTab === 'daa'}
              badge={unread('daa')}
              onClick={() => setTab('daa')}
            />
            <TabPill
              label="Airband Radio"
              on={effectiveTab === 'airband'}
              badge={unread('airband')}
              onClick={() => setTab('airband')}
            />
          </>
        )}
        {LEGACY_PANEL_TABS && (
          <>
            <TabPill
              label="Events"
              on={effectiveTab === 'events'}
              badge={unread('events')}
              onClick={() => setTab('events')}
            />
            <TabPill
              label="Alerts"
              on={effectiveTab === 'alerts'}
              badge={unread('alerts')}
              onClick={() => setTab('alerts')}
            />
            <TabPill
              label="AI Intelligence"
              on={effectiveTab === 'ai'}
              badge={0}
              onClick={() => setTab('ai')}
            />
          </>
        )}
        {debugOn && (
          <TabPill
            label="Debug Logs"
            on={effectiveTab === 'debug'}
            badge={0}
            onClick={() => setTab('debug')}
          />
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {effectiveTab === 'daa' ? (
          <DaaAlertsPane />
        ) : effectiveTab === 'airband' ? (
          <AirbandPane />
        ) : effectiveTab === 'events' ? (
          <EventsPane />
        ) : effectiveTab === 'alerts' ? (
          <AlertsPane />
        ) : effectiveTab === 'debug' ? (
          <DebugPanel ctx={ctx} />
        ) : ctx === 'inv' ? (
          <InventoryIntelligence />
        ) : ctx === 'apps' ? (
          <AppsIntelligence />
        ) : (
          <ServicesIntelligence />
        )}
      </div>
    </div>
  );
}

/** Mockup `.tgl-radio` — same pill selector used by the SERVICE radios. */
function TabPill({
  label,
  on,
  badge,
  onClick,
}: { label: string; on: boolean; badge: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'flex items-center h-7 px-2.5 rounded-[14px] border text-[0.769em] font-semibold font-mono whitespace-nowrap select-none transition-colors',
        on
          ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
          : 'bg-(--color-bg) border-(--color-border) text-(--color-text2) hover:border-(--color-green)',
      )}
    >
      {label}
      {badge > 0 && (
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-[9px] bg-(--color-red) text-(--color-on-accent) text-[0.769em] font-bold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
